import { createFileRoute } from '@tanstack/react-router';
import Cookies from 'js-cookie';
import Button from '../../components/button/button';
import { seo } from '../../utils/seo';
import { consentCookieName, gaOptOut } from '../../utils/tracking';

export const Route = createFileRoute('/_layout/legal/cookie-policy')({
  head: () => ({ meta: seo({ title: 'Süti szabályzat' }) }),
  component: CookiePolicyRoute
});

function CookiePolicyRoute() {
  const path = '/';
  const domains = [
    'pixwrist.hu',
    'pixwrist.com',
    'azurestaticapps.net',
    'localhost'
  ];

  const revoke = () => {
    // Opt out from Google Analytics
    gaOptOut();

    // Remove the tracking cookies (see the tables below): Google Analytics (_ga, _ga_<container id>, and the old
    // _gid, _gat*), Hotjar (_hj*, some with the site id in their name) and Application Insights (ai_*).
    // They are removed both as host-only and as domain cookies (Google Analytics and Hotjar set them on the domain).
    const trackingCookieNames = Object.keys(Cookies.get()).filter(name =>
      /^(_ga|_gid|_gat|_hj|ai_)/.test(name)
    );

    trackingCookieNames.forEach(name => {
      Cookies.remove(name, { path });
      domains.forEach(domain => Cookies.remove(name, { path, domain }));
    });

    // Remove Hotjar's and Application Insights' storage items
    localStorage.removeItem('hjActiveViewportIds');
    sessionStorage.removeItem('hjViewportId');
    // AI_buffer_<version>, AI_sentBuffer_<version> (the version of the SDK's storage format)
    Object.keys(sessionStorage)
      .filter(key => /^AI_(sent)?[bB]uffer/.test(key))
      .forEach(key => sessionStorage.removeItem(key));

    // Remove consent cookie
    Cookies.remove(consentCookieName);

    // Redirect to home page
    window.location.href = '/';
  };

  return (
    <div className="container-fluid">
      <div className="page-content centered">
        <h1 className="page-title">Süti szabályzat</h1>
        <p>
          Weboldalunk megfelelő működése érdekében bizonyos esetekben apró adat
          fájlokat helyezünk el a Felhasználó számítógépes eszközén, a legtöbb
          modern honlaphoz hasonlóan.
        </p>

        <h2>Mi az a süti?</h2>
        <p>
          A süti egy olyan, kisméretű szöveges fájl, amit a honlap a Felhasználó
          számítógépes eszközére (beleértve a mobil telefonokat is) helyez el.
          Ennek köszönhetően a honlap „emlékezni” tud a Felhasználó
          beállításaira (pl.: használt nyelv, betűméret, megjelenítés, stb.),
          így nem kell azt minden alkalommal újra beállítani, amikor felkeresi
          weboldalunkat.
        </p>
        <p>
          Ezek a sütik letörölhetőek, vagy blokkolhatóak, de ebben az esetben a
          Weboldal nem biztos, hogy megfelelően fog működni. A sütiket nem
          használjuk arra, hogy a Felhasználót személy szerint beazonosítsuk.
          Ezek a sütik csak a fent leírt célokat szolgálják.
        </p>

        <h2>A weboldalon használt sütik</h2>
        <h3>Működéshez szükséges sütik</h3>
        <p>
          A működéshez szükséges sütik a weboldal használatát segítik azzal,
          hogy alapvető funkciókat aktiválnak. A weboldal nem tud helyesen
          működni ezen sütik nélkül.
        </p>
        <table className="table">
          <thead>
            <tr>
              <th>Név</th>
              <th>Szolgáltató</th>
              <th>Cél</th>
              <th>Lejárat</th>
              <th>Típus</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>cookie-consent</td>
              <td>www.pixwrist.hu</td>
              <td>A látogató cookie nyilatkozatának tárolására szolgál.</td>
              <td>1 év</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>ga-disable-&lt;mérési azonosító&gt;</td>
              <td>www.pixwrist.hu</td>
              <td>
                A süti nyilatkozat visszavonása esetén tárolja, hogy a látogató
                letiltotta a Google Analytics használatát.
              </td>
              <td>10 év</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>cartId</td>
              <td>www.pixwrist.hu</td>
              <td>A látogató kosarának megjegyzésére szolgál.</td>
              <td>Persistent</td>
              <td>HTML Local Storage</td>
            </tr>
            <tr>
              <td>pixwristFavourites</td>
              <td>www.pixwrist.hu</td>
              <td>A látogató kedvenc termékei megjegyzésére szolgál.</td>
              <td>Persistent</td>
              <td>HTML Local Storage</td>
            </tr>
            <tr>
              <td>theme</td>
              <td>www.pixwrist.hu</td>
              <td>
                A látogató által választott (világos vagy sötét) megjelenés
                megjegyzésére szolgál.
              </td>
              <td>Persistent</td>
              <td>HTML Local Storage</td>
            </tr>
            <tr>
              <td>news-banner-&lt;azonosító&gt;</td>
              <td>www.pixwrist.hu</td>
              <td>
                Azt tárolja, hogy a látogató bezárta-e az adott hírsávot, hogy
                az ne jelenjen meg újra.
              </td>
              <td>Persistent</td>
              <td>HTML Local Storage</td>
            </tr>
          </tbody>
        </table>

        <h3>Statisztikai sütik</h3>
        <p>
          Az adatok névtelen formában való gyűjtésén és jelentésén keresztül a
          statisztikai sütik segítenek a weboldal látogatottsági statisztika
          felállításában, valamint segítenek a weboldal tulajdonosának
          megérteni, hogyan lépnek interakcióba a látogatók a weboldallal. Az
          adatok nem kapcsolódnak konkrét személyhez.
        </p>
        <table className="table">
          <thead>
            <tr>
              <th>Név</th>
              <th>Szolgáltató</th>
              <th>Cél</th>
              <th>Lejárat</th>
              <th>Típus</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>_ga</td>
              <td>www.pixwrist.hu</td>
              <td>
                A Google Analytics egy egyedi azonosítót regisztrál, amely
                statisztikai adatok előállítására szolgál arra vonatkozóan, hogy
                a látogató hogyan használja a weboldalt.
              </td>
              <td>2 év</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>_ga_&lt;tároló-azonosító&gt;</td>
              <td>www.pixwrist.hu</td>
              <td>
                A Google Analytics használja a látogatói munkamenet állapotának
                megőrzésére.
              </td>
              <td>2 év</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>_hjSessionUser_&lt;webhely-azonosító&gt;</td>
              <td>www.pixwrist.hu</td>
              <td>
                Hotjar süti, amelyet akkor állítanak be, amikor a látogató
                először a Hotjar szkriptet tartalmazó oldalra kerül. Az adott
                webhelyhez tartozó Hotjar felhasználói azonosító megőrzésére
                szolgál, hogy a későbbi látogatások adatai ugyanahhoz a
                felhasználói azonosítóhoz tartozzanak. A Hotjar nem követi a
                felhasználókat különböző webhelyek között.
              </td>
              <td>1 év</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>_hjSession_&lt;webhely-azonosító&gt;</td>
              <td>www.pixwrist.hu</td>
              <td>
                Hotjar süti, amely az aktuális munkamenet adatait tárolja, hogy
                a munkamenet későbbi kérései ugyanahhoz a munkamenethez
                tartozzanak.
              </td>
              <td>30 perc (aktivitás esetén meghosszabbodik)</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>_hjHasCachedUserAttributes</td>
              <td>www.pixwrist.hu</td>
              <td>
                Hotjar süti, amely azt jelzi, hogy a Hotjar által a böngészőben
                tárolt felhasználói attribútumok naprakészek-e.
              </td>
              <td>Session</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>_hjUserAttributesHash</td>
              <td>www.pixwrist.hu</td>
              <td>
                Hotjar süti, amely alapján a Hotjar észleli, ha valamelyik
                felhasználói attribútum megváltozott és frissíteni kell.
              </td>
              <td>2 perc (30 másodpercenként meghosszabbodik)</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>_hjCookieTest</td>
              <td>www.pixwrist.hu</td>
              <td>
                Hotjar süti, amellyel a Hotjar szkript ellenőrzi, hogy
                használhat-e sütiket. A létrehozása után szinte azonnal
                törlődik.
              </td>
              <td>Session (100 ms-on belül törlődik)</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>_hjLocalStorageTest</td>
              <td>www.pixwrist.hu</td>
              <td>
                Hotjar süti, amellyel a Hotjar szkript ellenőrzi, hogy
                használhatja-e a böngésző Local Storage tárhelyét. A létrehozása
                után szinte azonnal törlődik.
              </td>
              <td>100 ms-on belül törlődik</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>_hjSessionStorageTest</td>
              <td>www.pixwrist.hu</td>
              <td>
                Hotjar süti, amellyel a Hotjar szkript ellenőrzi, hogy
                használhatja-e a böngésző Session Storage tárhelyét. A
                létrehozása után szinte azonnal törlődik.
              </td>
              <td>100 ms-on belül törlődik</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>hjViewportId</td>
              <td>www.pixwrist.hu</td>
              <td>
                A Hotjar a látogató képernyőjének (nézetének) adatait, például
                méreteit tárolja benne.
              </td>
              <td>Session</td>
              <td>HTML Session Storage</td>
            </tr>
            <tr>
              <td>hjActiveViewportIds</td>
              <td>www.pixwrist.hu</td>
              <td>
                A Hotjar a látogató aktív nézeteinek azonosítóit tárolja benne.
                Az oldal bezárásakor törlődik.
              </td>
              <td>Session</td>
              <td>HTML Local Storage</td>
            </tr>
            <tr>
              <td>_hjTLDTest</td>
              <td>www.pixwrist.hu</td>
              <td>
                Amikor a Hotjar szkript fut, megpróbáljuk meghatározni a
                legáltalánosabb cookie-útvonalat, amelyet használnunk kell, az
                oldal hosztneve helyett. Ez azért történik, hogy a sütik
                megoszthatók legyenek az aldomainek között (adott esetben).
                Ennek megállapításához megpróbáljuk a _hjTLDTest cookie-t
                tárolni a különböző URL-szubsztring alternatívák számára, amíg
                meg nem sikerül. Ezen ellenőrzés után a cookie eltávolításra
                kerül.
              </td>
              <td>Session</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>AI_buffer_&lt;verziószám&gt;</td>
              <td>www.pixwrist.hu</td>
              <td>
                Az "AI_sentBuffer_&lt;verziószám&gt;"-rel összefüggésben
                használják az adatkiszolgáló-frissítések (Azure) számának
                korlátozása érdekében. Ez a szinergia lehetővé teszi a webhely
                számára, hogy észlelje az adat-szerver-frissítések
                ismétlődéseit.
              </td>
              <td>Session</td>
              <td>HTML Session Storage</td>
            </tr>
            <tr>
              <td>AI_sentBuffer_&lt;verziószám&gt;</td>
              <td>www.pixwrist.hu</td>
              <td>
                Az "AI_buffer_&lt;verziószám&gt;"-rel összefüggésben használják
                az adatkiszolgáló-frissítések (Azure) számának korlátozása
                érdekében. Ez a szinergia lehetővé teszi a webhely számára, hogy
                észlelje az adat-szerver-frissítések ismétlődéseit.
              </td>
              <td>Session</td>
              <td>HTML Session Storage</td>
            </tr>
            <tr>
              <td>ai_user</td>
              <td>www.pixwrist.hu</td>
              <td>
                Ez a cookie a Microsoft Application Insights szoftverhez van
                társítva, amely statisztikai használati és telemetriai
                információkat gyűjt az Azure felhő platformra épített
                alkalmazásokhoz. Ez egy egyedi felhasználói azonosító cookie,
                amely lehetővé teszi az alkalmazáshoz idővel hozzáférő
                felhasználók számának megszámlálását.
              </td>
              <td>1 év</td>
              <td>HTTP Cookie</td>
            </tr>
            <tr>
              <td>ai_session</td>
              <td>www.pixwrist.hu</td>
              <td>
                Ez a cookie a Microsoft Application Insights szoftverhez van
                társítva, amely statisztikai használati és telemetriai
                információkat gyűjt az Azure felhő platformra épített
                alkalmazásokhoz. Ez egy egyedi névtelen munkamenet-azonosító
                süti.
              </td>
              <td>30 perc inaktivitás után, legfeljebb 24 óra</td>
              <td>HTTP Cookie</td>
            </tr>
          </tbody>
        </table>
        <p>
          További információ:
          <br />
          <a
            href="https://support.google.com/analytics/answer/11397207"
            target="_blank"
          >
            https://support.google.com/analytics/answer/11397207
          </a>
          <br />
          <a
            href="https://help.hotjar.com/hc/en-us/articles/36819973371409-Cookies-Set-by-the-Hotjar-Tracking-Code"
            target="_blank"
          >
            https://help.hotjar.com/hc/en-us/articles/36819973371409-Cookies-Set-by-the-Hotjar-Tracking-Code
          </a>
          <br />
          <a
            href="https://learn.microsoft.com/azure/azure-monitor/app/javascript-sdk-configuration"
            target="_blank"
          >
            https://learn.microsoft.com/azure/azure-monitor/app/javascript-sdk-configuration
          </a>
        </p>

        <h2>Google Analytics</h2>
        <p>
          A Weboldal a Google Analytics alkalmazást használja, amely a Google
          Inc. („Google”) webelemző szolgáltatása. A Google Analytics
          úgynevezett „cookie-kat”, szövegfájlokat használ, amelyeket a
          számítógépére mentenek, így elősegítik Felhasználó által látogatott
          weblap használatának elemzését.
        </p>
        <p>
          A Felhasználó által használt weboldallal kapcsolatos cookie-kkal
          létrehozott információk rendszerint a Google egyik USA-beli szerverére
          kerülnek és tárolódnak. A Weboldal a Google Analytics 4 verzióját
          használja, amely a Felhasználó IP-címét nem naplózza és nem tárolja.
        </p>
        <p>
          Megbízásunkból a Google ezeket az információkat arra fogja használni,
          hogy kiértékelje, hogyan használta a Felhasználó a honlapot, továbbá,
          hogy részünkre a honlap aktivitásával összefüggő jelentéseket
          készítsen, valamint, hogy a weboldal- és az internethasználattal
          kapcsolatos további szolgáltatásokat teljesítsen.
        </p>
        <p>
          A Google Analytics keretein belül a Felhasználó böngészője által
          továbbított IP-címet nem vezeti össze a Google más adataival. A
          cookie-k tárolását a Felhasználó a böngészőjének megfelelő
          beállításával megakadályozhatja, azonban felhívjuk figyelmét, hogy
          ebben az esetben előfordulhat, hogy ennek a honlapnak nem minden
          funkciója lesz teljes körűen használható. Megakadályozhatja továbbá,
          hogy a Google gyűjtse és feldolgozza a cookie-k általi, a Felhasználó
          weboldalhasználattal kapcsolatos adatait (beleértve az IP-címet is),
          ha letölti és telepíti a következő linken elérhető böngésző plugint.
        </p>
        <p>
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank">
            https://tools.google.com/dlpage/gaoptout
          </a>
        </p>

        <h2>Hogyan kezelhetőek a sütik?</h2>
        <p>
          A süti fájlok törölhetőek vagy a legtöbb mai böngészővel elhelyezésük
          blokkolható is. Ebben az esetben azonban a weboldalunk használatakor
          bizonyos beállításokat minden alkalommal újra el kell végezni, és
          bizonyos szolgáltatások nem feltétlenül fognak működni.
        </p>
        <p>
          A sütik törléséről, blokkolásáról részletes információk találhatóak a{' '}
          <a href="https://www.allaboutcookies.org" target="_blank">
            https://www.allaboutcookies.org
          </a>{' '}
          oldalon és a Felhasználó által használt böngészőre vonatkozóan az
          alábbi linkeken:
        </p>
        <ul>
          <li>
            <a
              href="https://support.google.com/chrome/answer/95647?hl=hu"
              target="_blank"
            >
              Google Chrome
            </a>
          </li>
          <li>
            <a
              href="https://support.mozilla.org/hu/kb/weboldalak-altal-elhelyezett-sutik-torlese-szamito"
              target="_blank"
            >
              Mozilla Firefox
            </a>
          </li>
          <li>
            <a
              href="https://support.microsoft.com/hu-hu/help/17442/windows-internet-explorer-delete-manage-cookies"
              target="_blank"
            >
              Microsoft Internet Explorer
            </a>
          </li>
          <li>
            <a
              href="https://support.apple.com/hu-hu/guide/safari/sfri11471/mac"
              target="_blank"
            >
              Safari
            </a>
          </li>
          <li>
            <a
              href="https://help.opera.com/en/latest/web-preferences/#cookies"
              target="_blank"
            >
              Opera
            </a>
          </li>
        </ul>

        <h2>Nyilatkozat visszavonása</h2>
        <p>
          Az alábbi gomb megnyomásával tudod a süti nyilatkozatod visszavonni az
          adott böngészőben. A gomb megnyomása után átirányítunk a főoldalra,
          ahol újra lehetőséged lesz nyilatkozatot tenni.
        </p>
        <Button variant="secondary" onClick={revoke}>
          Nyilatkozat visszavonása
        </Button>
      </div>
    </div>
  );
}
